import axios from 'axios';

// On retire la constante statique pour la rendre dynamique
const BASE_SYSTEM_PROMPT = `
Tu es l'Architecte FlexiHub.

RÈGLES ABSOLUES :
1. Pour poser des questions : Utilise le format JSON "Widget UI" (type: "form").
2. Pour générer le container final : Utilise le format JSON "Configuration" ci-dessous.
3. Entoure TOUJOURS ton JSON avec \`\`\`json ... \`\`\`

⚠️ FORMAT OBLIGATOIRE (NE PAS INVENTER D'AUTRES CLÉS) :
\`\`\`json
{
  "metadata": { 
    "title": "Titre du livre", 
    "description": "Description...",
    "backgroundColor": "#ffffff" 
  },
  "header": { 
    "text": "<h1>Titre</h1>", 
    "backgroundColor": "#ffffff", 
    "height": 100
  },
  "accordion": {
    "tabs": [
      { 
        "title": "Titre Onglet 1", 
        "content": "<p>Contenu HTML...</p>" 
      }
    ],
    "tabTitleFormat": { "color": "#000000", "backgroundColor": "#ffffff" }
  },
  "footer": {  // ✅ AJOUT DU FOOTER
    "text": "<p>© 2025 Mon Entreprise - <a href='#'>Mentions Légales</a></p>",
    "backgroundColor": "#f8f9fa",
    "height": 60
  }
}
\`\`\`

NE JAMAIS METTRE "tabs" À LA RACINE. ILS DOIVENT ÊTRE DANS "accordion".

--- GESTION DES PLUGINS ---
Tu as accès à une bibliothèque de plugins interactifs.
Pour insérer un plugin dans le contenu HTML d'un onglet ("content"), tu DOIS utiliser EXACTEMENT ce format HTML :

<div class="plugin-placeholder" data-plugin="ID_DU_PLUGIN" data-plugin-id="UNIQUE_ID" data-config='JSON_DE_CONFIG'></div>

Règles :
- data-plugin : l'identifiant du plugin (ex: "audio-player-advanced", "blog-modular", "restaurant-modular")
- data-plugin-id : un identifiant unique pour cette instance (ex: "plugin-1", "plugin-abc123")
- data-config : la configuration JSON du plugin, avec les apostrophes comme délimiteurs (pas de guillemets doubles à l'extérieur)

Exemple pour un lecteur audio :
<div class="plugin-placeholder" data-plugin="audio-player-advanced" data-plugin-id="audio-1" data-config='{"title":"Nos Podcasts","theme":"dark","playlist":[{"title":"Épisode 1","artist":"Équipe","url":"https://example.com/ep1.mp3"}]}'></div>

NE JAMAIS inventer de balises <VideoPlugin /> ou autre. Utilise uniquement la div avec data-plugin.

LISTE DES PLUGINS DISPONIBLES ET LEUR CONFIGURATION :
{{PLUGINS_DOCS}}
`;

export const chatWithAI = async (messages: any[], _apiKeys?: any, modelProvider?: string, pluginsDocs?: string) => {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  const isLocal = modelProvider?.startsWith('LlamaCpp') || modelProvider?.startsWith('vLLM');

  // Pour les modèles locaux (llama.cpp / vLLM), utiliser fetch avec lecture SSE
  // pour éviter les timeouts sur les longues générations CPU.
  if (isLocal) {
    try {
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ messages, selectedModel: modelProvider, modelProvider, pluginsDocs }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errText}`);
      }

      const contentType = response.headers.get('content-type') || '';

      // Réponse SSE (streaming) — collecte tous les tokens
      if (contentType.includes('text/event-stream')) {
        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          for (const line of chunk.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            try {
              const parsed = JSON.parse(data);
              fullContent += parsed.choices?.[0]?.delta?.content || '';
            } catch {}
          }
        }
        return fullContent;
      }

      // Réponse JSON normale (mode non-streaming, ex: ContainerWizard JSON)
      const json = await response.json();
      return json.response || json.choices?.[0]?.message?.content || '';
    } catch (error) {
      console.error('Erreur chatWithAI (local):', error);
      throw error;
    }
  }

  // Pour les modèles cloud : axios classique
  try {
    const response = await axios.post('/api/ai/chat', {
      messages,
      selectedModel: modelProvider,
      modelProvider: modelProvider,
      pluginsDocs
    }, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return response.data.response || response.data.choices?.[0]?.message?.content;
  } catch (error) {
    console.error('Erreur chatWithAI:', error);
    throw error;
  }
};

// Fonction existante pour lancer la génération
export const generateAIMedia = async (
  prompt: string,
  type: 'image' | 'video',
  currentPath: string,
  contextFiles?: Array<{ name: string; url: string }>,
  model?: string,
  provider?: string,
  ltxOptions?: {
    duration?: number;
    fps?: number;
    resolution?: string;
    generateAudio?: boolean;
    retakeStartTime?: number;
    retakeDuration?: number;
    retakeMode?: string;
    useUpload?: boolean;
  },
  comfyOptions?: {
    duration?: number;
    resolution?: string;
    aspectRatio?: string;
    seed?: number;
    useI2V?: boolean;
    // legacy Wan2.1
    negativePrompt?: string;
    fps?: number;
    steps?: number;
    guidanceHigh?: number;
    guidanceLow?: number;
    scheduler?: string;
    flowShift?: number;
  }
) => {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await axios.post('/api/ai/generate-media', {
      prompt,
      type,
      currentPath,
      contextFiles: Array.isArray(contextFiles) ? contextFiles : undefined,
      model,
      provider,
      ltxOptions,
      comfyOptions
    }, { headers });

    return response.data;
  } catch (error) {
    console.error('Erreur generateAIMedia:', error);
    throw error;
  }
};

// ─── OpenJarvis — agent persistant avec mémoire FAISS ────────────────────────
// Maintient un agentId de session pour la continuité des échanges.
// Retourne { response, agentId } ou lève une erreur.
export const chatWithJarvis = async (
  message: string,
  agentId?: string | null
): Promise<{ response: string; agentId: string }> => {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  const res = await fetch('/api/ai/jarvis/agent', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message, agentId: agentId || undefined }),
  });
  if (!res.ok) throw new Error(`Jarvis HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return { response: data.response, agentId: data.agentId };
};

// ✅ NOUVELLE FONCTION AJOUTÉE : Polling pour Sora
export const checkAIMediaStatus = async (jobId: string) => {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const encodedJobId = encodeURIComponent(jobId);
    const response = await axios.get(`/api/ai/media-status/${encodedJobId}`, { headers });
    return response.data;
  } catch (error) {
    console.error('Erreur checkAIMediaStatus:', error);
    throw error;
  }
};
